// Two-line measurements whose second line is drawn on another viewport's image (IMEXHS
// "Anteversión Femoral": femoral neck axis on one slice, posterior condylar line on another).
//
// The measurement ("owner") lives in the tool state of the first line's image and keeps both lines'
// handles. When line 2 is drawn on a different image, a "proxy" entry is added to that image's tool
// state: its start/end ARE the owner's start2/end2 objects, so dragging the proxy there moves the
// owner's line. Each image shows only its own line; the angle is shown on both. Removing either
// removes both. Proxies are display-only: never saved, never listed in the workspace.
// Like IMEXHS, the stored annotation is the owner (one image, 8 numbers): reopened, both lines show
// on the owner's image.
import { csCore, csTools, type Handle, type MeasurementData } from './cstypes';

const PROXY_KEY = 'crossProxyOf';
const PARTNER_KEY = 'crossProxy';

/** A proxy drawn on the second image, pointing at its owner measurement. */
export function isCrossProxy(data: MeasurementData): boolean {
  return typeof data[PROXY_KEY] === 'object' && data[PROXY_KEY] !== null;
}

export function proxyOwner(data: MeasurementData): MeasurementData | undefined {
  return isCrossProxy(data) ? (data[PROXY_KEY] as MeasurementData) : undefined;
}

/** Whether the owner's second line lives on another image (shown there by its proxy). */
export function isCrossImageOwner(data: MeasurementData): boolean {
  return typeof data[PARTNER_KEY] === 'object' && data[PARTNER_KEY] !== null;
}

type ImageState = Record<string, { data: MeasurementData[] } | undefined>;

function stateFor(imageId: string): ImageState | undefined {
  return csTools().globalImageIdSpecificToolStateManager.saveToolState()[imageId];
}

/** The incomplete measurement of `toolName` on any image, with the image it belongs to. */
export function findPendingAnywhere(toolName: string): { data: MeasurementData; imageId: string } | undefined {
  const all = csTools().globalImageIdSpecificToolStateManager.saveToolState();
  for (const [imageId, imageState] of Object.entries(all)) {
    const data = imageState?.[toolName]?.data.find((d) => d.complete === false && !isCrossProxy(d));
    if (data) return { data, imageId };
  }
  return undefined;
}

/** Links a proxy (on `proxyImageId`) to its owner (on `ownerImageId`). */
export function linkCrossImage(
  owner: MeasurementData,
  ownerImageId: string,
  proxyImageId: string,
  textBox: Handle,
): MeasurementData {
  const proxy: MeasurementData = {
    visible: true,
    active: true,
    complete: true,
    handles: { start: owner.handles.start2, end: owner.handles.end2, textBox },
    [PROXY_KEY]: owner,
    crossImageId: ownerImageId,
  };
  owner[PARTNER_KEY] = proxy;
  owner.crossImageId = proxyImageId;
  return proxy;
}

function contains(imageId: unknown, toolName: string, data: MeasurementData): boolean {
  return typeof imageId === 'string' && (stateFor(imageId)?.[toolName]?.data.includes(data) ?? false);
}

function removeFrom(imageId: unknown, toolName: string, data: MeasurementData): void {
  if (typeof imageId !== 'string') return;
  const list = stateFor(imageId)?.[toolName]?.data;
  const index = list?.indexOf(data) ?? -1;
  if (list && index >= 0) list.splice(index, 1);
}

/**
 * Keeps an owner/proxy pair consistent before it is drawn: when one half was erased or cleared, the
 * other is removed too. Returns false when `data` was removed (do not draw it).
 */
export function keepPairConsistent(toolName: string, data: MeasurementData, imageId: string): boolean {
  const owner = proxyOwner(data);
  if (owner) {
    if (contains(data.crossImageId, toolName, owner)) return true;
    removeFrom(imageId, toolName, data);
    return false;
  }
  if (!isCrossImageOwner(data)) return true;
  const proxy = data[PARTNER_KEY] as MeasurementData;
  if (contains(data.crossImageId, toolName, proxy)) return true;
  removeFrom(imageId, toolName, data);
  return false;
}

/** Redraws every viewport, so both halves of a pair follow a change on either. */
export function updateAllViewports(): void {
  const core = csCore() as unknown as { getEnabledElements(): Array<{ element: HTMLElement; image?: unknown }> };
  for (const { element, image } of core.getEnabledElements()) if (image) csCore().updateImage(element);
}
